import { useCallback, useEffect, useState } from "react";
import { checkMilestones, getNewMilestones } from "@hicya/habits-shared/engine";

import { useHabits } from "@/context/habits-context";
import { useDatabase } from "@/db/provider";

/**
 * Checks for newly achieved milestones and provides a queue for display.
 * Returns the next milestone ID to show and a dismiss function.
 */
export function useMilestones(): {
  currentMilestone: string | null;
  dismissMilestone: (id: string) => void;
} {
  const { state, updateSettings } = useHabits();
  const db = useDatabase();
  const [queue, setQueue] = useState<string[]>([]);

  useEffect(() => {
    if (state.phase !== "ready") return;
    if (!state.settings.onboardingCompleted) return;

    let disposed = false;

    async function check() {
      const allCompletions = await db.getAllCompletions();
      if (disposed) return;

      const achieved = checkMilestones(
        state.habits,
        allCompletions,
        state.today,
        state.settings.firstDayOfWeek,
        state.settings.graceDaysPerWeek,
      );

      const newMilestones = getNewMilestones(achieved, state.settings.dismissedMilestones);
      if (newMilestones.length > 0) {
        setQueue(newMilestones);
      }
    }

    void check();
    return () => {
      disposed = true;
    };
  }, [db, state.phase, state.habits, state.today, state.settings, state.completions]);

  const dismissMilestone = useCallback(
    (id: string) => {
      const updated = [...state.settings.dismissedMilestones, id];
      void updateSettings("dismissedMilestones", JSON.stringify(updated));
      setQueue((prev) => prev.filter((m) => m !== id));
    },
    [state.settings.dismissedMilestones, updateSettings],
  );

  return {
    currentMilestone: queue.length > 0 ? queue[0] : null,
    dismissMilestone,
  };
}
